/**
 * MetaDashboardCacheService — V3 Cosmos cache.
 *
 * The frontend NEVER touches Cosmos directly. All reads/writes go through
 * the backend `/api/v3/*` endpoints. This service is the single FE boundary
 * between application code and the V3 storage layer.
 *
 * Storage shape (Cosmos PascalCase, except `id`):
 *   METADASHBOARDV3 — meta hierarchy (no inline widget bodies). One doc per
 *                    metaId, partition `/Type = METADASHBOARDV3`.
 *   WIDGETV3        — full widget body, no metaId on envelope. One doc per
 *                    widgetId, partition `/Type = WIDGETV3`.
 *
 * Wire shape: the API serializes responses with JsonNamingPolicy.CamelCase
 * (see HttpResponseExtensions.CamelCaseJsonOptions), so the FE receives
 * camelCase JSON. Request bodies sent FROM the FE are also camelCase; the
 * backend's Newtonsoft deserializer matches them case-insensitively to the
 * PascalCase C# properties. NO key conversion is needed in this service —
 * the API handles PascalCase ↔ camelCase at the boundary.
 *
 * The renderer + factories continue to consume the existing camelCase
 * `MetaDashboard` / `Widget` shape (from meta-dashboard.types.ts). The cache
 * reassembles V3 docs into that shape at refresh time. V3 envelope types
 * are defined in meta-dashboard-v3.types.ts.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, shareReplay, tap } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { MetaDashboard, Widget } from '../models/meta-dashboard.types';
import {
    MetaDashboardV3Doc,
    MetaDashboardV3Body,
    MetaDashboardV3UpsertRequest,
    WidgetV3Doc,
    WidgetV3Body,
    WidgetV3UpsertRequest,
} from '../models/meta-dashboard-v3.types';

interface ApiResponse<T> {
    success: boolean;
    data:    T;
    message?: string;
}

export type CacheStatus = 'idle' | 'loading' | 'ready' | 'error' | 'empty';

/**
 * Bumped whenever the persisted shape (V3/V4 envelope, body fields) changes.
 * Stale caches with a different version are dropped on read.
 */
const CACHE_VERSION = 4;
const CACHE_KEY     = 'tma:meta-cache:v4';

interface PersistedCache {
    version: number;
    savedAt: string;
    metas:   Array<{ metaId: string;   body: MetaDashboardV3Body }>;
    widgets: Array<{ widgetId: string; body: WidgetV3Body }>;
}

@Injectable({ providedIn: 'root' })
export class MetaDashboardCacheService {
    private readonly http   = inject(HttpClient);
    private readonly apiUrl = environment.apiUrl;

    // ── Internal stores ──────────────────────────────────────────────
    // Hydrated synchronously from localStorage at construction time so that
    // a Cosmos-source toggle has immediate data on first paint, even before
    // the HTTP refresh resolves. The refresh always overwrites with fresh data.
    private readonly _metaBodyMap = signal<Map<string, MetaDashboardV3Body>>(new Map());
    private readonly _widgetBodyMap = signal<Map<string, WidgetV3Body>>(new Map());
    /** widgetId → metaId(s) it appears in. Derived from dashboards[].widgets[].widgetId. */
    private readonly _ownerMap = signal<Map<string, string[]>>(new Map());

    private readonly _status     = signal<CacheStatus>('idle');
    private readonly _lastLoaded = signal<Date | null>(null);
    private readonly _lastError  = signal<string | null>(null);
    /** True when this session's signals were seeded from localStorage (vs. HTTP). */
    private readonly _hydratedFromStorage = signal<boolean>(false);
    readonly hydratedFromStorage = this._hydratedFromStorage.asReadonly();

    constructor() {
        this.hydrateFromStorage();
    }

    readonly status     = this._status.asReadonly();
    readonly lastLoaded = this._lastLoaded.asReadonly();
    readonly lastError  = this._lastError.asReadonly();

    /**
     * Reassembled, renderer-facing map: metaId → MetaDashboard with `widgets: { id: Widget }`
     * populated by joining METADASHBOARDV3 + WIDGETV3 docs. The renderer + tester
     * read this signal; they don't see the V3 envelope split.
     */
    readonly map = computed<Map<string, MetaDashboard>>(() => {
        const out = new Map<string, MetaDashboard>();
        const metaBodies = this._metaBodyMap();
        const widgetBodies = this._widgetBodyMap();

        for (const [metaId, metaBody] of metaBodies) {
            const widgetsForMeta: Record<string, Widget> = {};
            for (const dash of metaBody.dashboards ?? []) {
                for (const placement of dash.widgets ?? []) {
                    const wb = widgetBodies.get(placement.widgetId);
                    if (wb) widgetsForMeta[placement.widgetId] = this.toRendererWidget(wb);
                }
            }
            out.set(metaId, this.toRendererMeta(metaBody, widgetsForMeta));
        }
        return out;
    });

    readonly count    = computed(() => this.map().size);
    readonly isReady  = computed(() => this._status() === 'ready');
    readonly isLoaded = computed(() => {
        const s = this._status();
        return s === 'ready' || s === 'empty' || s === 'error';
    });

    // ── Bootstrap + refresh ──────────────────────────────────────────

    /** APP_INITIALIZER entry point. Always resolves — never blocks app startup. */
    bootstrap(): Promise<void> {
        return new Promise(resolve => {
            this.refresh().subscribe({ next: () => resolve(), error: () => resolve() });
        });
    }

    private _refresh$: Observable<MetaDashboard[]> | null = null;

    /** Pull both METADASHBOARDV3 + WIDGETV3 in parallel. Concurrent calls share. */
    refresh(): Observable<MetaDashboard[]> {
        if (this._refresh$) return this._refresh$;

        this._status.set('loading');
        this._lastError.set(null);

        // The API serializes Cosmos PascalCase to camelCase JSON via
        // JsonNamingPolicy.CamelCase (see HttpResponseExtensions). FE just
        // reads camelCase per the V3 TS interfaces — no conversion here.
        const metas$ = this.http
            .get<ApiResponse<MetaDashboardV3Doc[]>>(`${this.apiUrl}/v3/meta-dashboards?include=body`)
            .pipe(map(r => r?.data ?? []));

        const widgets$ = this.http
            .get<ApiResponse<WidgetV3Doc[]>>(`${this.apiUrl}/v3/widgets`)
            .pipe(map(r => r?.data ?? []));

        this._refresh$ = forkJoin({ metas: metas$, widgets: widgets$ }).pipe(
            tap(({ metas, widgets }) => {
                const metaMap = new Map<string, MetaDashboardV3Body>();
                for (const m of metas) {
                    if (!m?.metaId || !m.body) continue;
                    metaMap.set(m.metaId, m.body);
                }
                const widgetMap = new Map<string, WidgetV3Body>();
                for (const w of widgets) {
                    if (!w?.widgetId || !w.body) continue;
                    widgetMap.set(w.widgetId, w.body);
                }
                this._metaBodyMap.set(metaMap);
                this._widgetBodyMap.set(widgetMap);
                this._ownerMap.set(this.computeOwnerMap(metaMap));
                this._status.set(metaMap.size === 0 && widgetMap.size === 0 ? 'empty' : 'ready');
                this._lastLoaded.set(new Date());
                this.persistToStorage(metaMap, widgetMap);
            }),
            map(() => Array.from(this.map().values())),
            catchError(err => {
                const msg = err?.error?.message ?? err?.message ?? 'V3 cache refresh failed';
                console.warn('[MetaDashboardCacheService] refresh failed (non-fatal):', msg);
                this._status.set('error');
                this._lastError.set(msg);
                return of<MetaDashboard[]>([]);
            }),
            finalize(() => { this._refresh$ = null; }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
        return this._refresh$;
    }

    /** Compute widgetId → list of metaIds that reference it. */
    private computeOwnerMap(metaMap: Map<string, MetaDashboardV3Body>): Map<string, string[]> {
        const out = new Map<string, string[]>();
        for (const [metaId, metaBody] of metaMap) {
            for (const dash of metaBody.dashboards ?? []) {
                for (const placement of dash.widgets ?? []) {
                    const owners = out.get(placement.widgetId) ?? [];
                    if (!owners.includes(metaId)) owners.push(metaId);
                    out.set(placement.widgetId, owners);
                }
            }
        }
        return out;
    }

    // ── Lookup ───────────────────────────────────────────────────────

    /** Reassembled MetaDashboard for the renderer (with widgets map). */
    get(metaId: string): MetaDashboard | null {
        return this.map().get(metaId) ?? null;
    }

    /** Renderer-shape Widget by id. */
    getWidget(widgetId: string): Widget | null {
        const body = this._widgetBodyMap().get(widgetId);
        return body ? this.toRendererWidget(body) : null;
    }

    /** Raw V3/V4 widget body (carries status, dataProcess, goldShape). Null if not cached. */
    getWidgetBody(widgetId: string): WidgetV3Body | null {
        return this._widgetBodyMap().get(widgetId) ?? null;
    }

    /**
     * First metaId that contains this widget, if any. Widgets can technically
     * appear under multiple metas; this returns the first owner found.
     */
    getOwnerMetaId(widgetId: string): string | null {
        const owners = this._ownerMap().get(widgetId);
        return owners?.[0] ?? null;
    }

    // ── Writes ───────────────────────────────────────────────────────

    /**
     * Upsert a single widget. The renderer-shape Widget is converted back to
     * the V3 body shape here; backend validators enforce dataProcess invariants.
     * `metaId` is informational — V3 widget docs don't store it on the envelope,
     * but we keep the parameter for API parity with V2.
     */
    upsertWidget(widget: Widget, _metaId: string, schemaVersion = 3): Observable<Widget> {
        const v3Body = this.toV3WidgetBody(widget);
        const body: WidgetV3UpsertRequest = {
            widgetId:      widget.id,
            body:          v3Body,
            schemaVersion,
        };
        // FE sends camelCase JSON; the C# Newtonsoft deserializer matches the
        // PascalCase properties case-insensitively. Cosmos stores PascalCase.
        return this.http
            .put<ApiResponse<WidgetV3Doc>>(`${this.apiUrl}/v3/widgets/${encodeURIComponent(widget.id)}`, body)
            .pipe(
                map(r => {
                    if (!r?.success) throw new Error(r?.message ?? 'Upsert widget failed');
                    return r.data.body;
                }),
                tap(saved => {
                    const wm = new Map(this._widgetBodyMap());
                    wm.set(widget.id, saved);
                    this._widgetBodyMap.set(wm);
                    this._lastLoaded.set(new Date());
                    this.persistToStorage(this._metaBodyMap(), wm);
                }),
                map(savedBody => this.toRendererWidget(savedBody)),
            );
    }

    /**
     * Upsert a widget straight from the V3 PascalCase doc body — used when
     * uploading a hand-authored / file-sourced JSON without round-tripping
     * through the renderer-shape Widget. The caller is responsible for shape
     * validity; backend validators still enforce dataProcess invariants.
     */
    upsertWidgetRaw(widgetId: string, rawBody: any, schemaVersion = 3): Observable<any> {
        const body: WidgetV3UpsertRequest = {
            widgetId,
            body:          rawBody,
            schemaVersion,
        };
        return this.http
            .put<ApiResponse<WidgetV3Doc>>(`${this.apiUrl}/v3/widgets/${encodeURIComponent(widgetId)}`, body)
            .pipe(
                map(r => {
                    if (!r?.success) throw new Error(r?.message ?? 'Upsert widget failed');
                    return r.data.body;
                }),
                tap(saved => {
                    const wm = new Map(this._widgetBodyMap());
                    wm.set(widgetId, saved);
                    this._widgetBodyMap.set(wm);
                    this._lastLoaded.set(new Date());
                    this.persistToStorage(this._metaBodyMap(), wm);
                }),
            );
    }

    /** Upsert just the meta hierarchy (no widget bodies). */
    upsertHierarchy(meta: MetaDashboard, schemaVersion = 3): Observable<MetaDashboard> {
        const v3Body = this.toV3MetaBody(meta);
        const body: MetaDashboardV3UpsertRequest = {
            metaId:        meta.id,
            title:         meta.title,
            body:          v3Body,
            schemaVersion,
        };
        return this.http
            .put<ApiResponse<MetaDashboardV3Doc>>(`${this.apiUrl}/v3/meta-dashboards/${encodeURIComponent(meta.id)}`, body)
            .pipe(
                map(r => {
                    if (!r?.success) throw new Error(r?.message ?? 'Upsert hierarchy failed');
                    return r.data.body;
                }),
                tap(saved => {
                    const mm = new Map(this._metaBodyMap());
                    mm.set(meta.id, saved);
                    this._metaBodyMap.set(mm);
                    this._ownerMap.set(this.computeOwnerMap(mm));
                    this._lastLoaded.set(new Date());
                    this.persistToStorage(mm, this._widgetBodyMap());
                }),
                map(() => meta),
            );
    }

    /** Convenience: upsert hierarchy + every widget body. Heavier write. */
    upsert(meta: MetaDashboard, schemaVersion = 3): Observable<MetaDashboard> {
        return this.upsertHierarchy(meta, schemaVersion).pipe(
            mergeMap(() => {
                const widgetEntries = Object.entries(meta.widgets ?? {});
                if (widgetEntries.length === 0) return of(meta);
                const calls = widgetEntries.map(([_id, w]) => this.upsertWidget(w, meta.id, schemaVersion));
                return forkJoin(calls).pipe(map(() => meta));
            }),
        );
    }

    /** Delete a widget doc. */
    deleteWidget(widgetId: string): Observable<void> {
        return this.http
            .delete<ApiResponse<unknown>>(`${this.apiUrl}/v3/widgets/${encodeURIComponent(widgetId)}`)
            .pipe(
                tap(() => {
                    const wm = new Map(this._widgetBodyMap()); wm.delete(widgetId); this._widgetBodyMap.set(wm);
                    this.persistToStorage(this._metaBodyMap(), wm);
                }),
                map(() => void 0),
                catchError(err => {
                    console.error('[MetaDashboardCacheService] deleteWidget failed:', err);
                    return throwError(() => err);
                }),
            );
    }

    /** Delete a meta hierarchy doc. Widget docs are NOT touched. */
    delete(metaId: string): Observable<void> {
        return this.http
            .delete<ApiResponse<unknown>>(`${this.apiUrl}/v3/meta-dashboards/${encodeURIComponent(metaId)}`)
            .pipe(
                tap(() => {
                    const mm = new Map(this._metaBodyMap()); mm.delete(metaId); this._metaBodyMap.set(mm);
                    this._ownerMap.set(this.computeOwnerMap(mm));
                    this.persistToStorage(mm, this._widgetBodyMap());
                }),
                map(() => void 0),
                catchError(err => {
                    console.error('[MetaDashboardCacheService] delete failed:', err);
                    return throwError(() => err);
                }),
            );
    }

    // ────────────────────────────────────────────────────────────────
    // localStorage persistence
    //
    // Hydrate at construction time so the first render has data even before
    // the HTTP refresh resolves. Persist after each successful refresh and
    // each upsert. Version-gated — bump CACHE_VERSION whenever the persisted
    // shape changes; older payloads are dropped on read.
    // ────────────────────────────────────────────────────────────────

    /** Read persisted snapshot into the signals. Silently ignored on any failure. */
    private hydrateFromStorage(): void {
        if (typeof localStorage === 'undefined') return;
        try {
            const raw = localStorage.getItem(CACHE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw) as PersistedCache;
            if (!parsed || parsed.version !== CACHE_VERSION) {
                localStorage.removeItem(CACHE_KEY);
                return;
            }
            const metaMap = new Map<string, MetaDashboardV3Body>();
            for (const m of parsed.metas ?? []) {
                if (m?.metaId && m.body) metaMap.set(m.metaId, m.body);
            }
            const widgetMap = new Map<string, WidgetV3Body>();
            for (const w of parsed.widgets ?? []) {
                if (w?.widgetId && w.body) widgetMap.set(w.widgetId, w.body);
            }
            if (metaMap.size === 0 && widgetMap.size === 0) return;
            this._metaBodyMap.set(metaMap);
            this._widgetBodyMap.set(widgetMap);
            this._ownerMap.set(this.computeOwnerMap(metaMap));
            this._lastLoaded.set(parsed.savedAt ? new Date(parsed.savedAt) : new Date());
            this._hydratedFromStorage.set(true);
            // Don't flip _status to 'ready' — refresh() will do that and the
            // network result is canonical. Keep status='idle' so UI knows we're
            // still about to fetch fresh data.
        } catch (err) {
            console.warn('[MetaDashboardCacheService] localStorage hydrate failed:', err);
            try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
        }
    }

    /** Write snapshot to localStorage. Silently ignored on quota / serialization failure. */
    private persistToStorage(
        metaMap: Map<string, MetaDashboardV3Body>,
        widgetMap: Map<string, WidgetV3Body>,
    ): void {
        if (typeof localStorage === 'undefined') return;
        try {
            const payload: PersistedCache = {
                version: CACHE_VERSION,
                savedAt: new Date().toISOString(),
                metas:   Array.from(metaMap.entries()).map(([metaId, body]) => ({ metaId, body })),
                widgets: Array.from(widgetMap.entries()).map(([widgetId, body]) => ({ widgetId, body })),
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
        } catch (err) {
            // Most likely QuotaExceeded — just log and continue, the in-memory
            // signals are still authoritative for this session.
            console.warn('[MetaDashboardCacheService] localStorage persist failed:', err);
        }
    }

    /** Drop the persisted snapshot. Useful for sign-out or schema-change recovery. */
    clearStorage(): void {
        if (typeof localStorage === 'undefined') return;
        try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
    }

    // ────────────────────────────────────────────────────────────────
    // Adapters: V3 ↔ renderer shape
    //
    // V3 body content is camelCase and matches the renderer's existing
    // MetaDashboard/Widget interfaces, so these are mostly identity casts.
    // Kept as explicit functions so the boundary is clear and any future
    // shape divergence has one place to handle it.
    // ────────────────────────────────────────────────────────────────

    private toRendererMeta(body: MetaDashboardV3Body, widgets: Record<string, Widget>): MetaDashboard {
        return {
            ...(body as unknown as MetaDashboard),
            widgets,
        };
    }

    private toRendererWidget(body: WidgetV3Body): Widget {
        // V3 body has the same camelCase shape the renderer expects.
        return body as unknown as Widget;
    }

    private toV3WidgetBody(w: Widget): WidgetV3Body {
        return w as unknown as WidgetV3Body;
    }

    private toV3MetaBody(meta: MetaDashboard): MetaDashboardV3Body {
        // Strip the widgets map — V3 hierarchy doesn't carry inline bodies.
        const { widgets: _drop, ...rest } = meta;
        return rest as unknown as MetaDashboardV3Body;
    }
}
